import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { take } from 'rxjs';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { CONSTANTS } from "../../../../../Constants/Constants";
import { TranslatePipe } from "@ngx-translate/core";
import { SolPropertiesService } from "../../../../../services/properties/sol-properties.service";
import { CustomerInformation, EstimateMetaData } from "../../../../../models/dtos";
import { IProposalCustomerInfoFormGroup } from "../../../../../models/properties/IProposalCustomerInfoFormGroup";
import { CommonDataService } from "../../../../../services/common-data.service";
@Component({
    selector: 'app-sol-properties-proposal-customer-info',
    templateUrl: './sol-properties-proposal-customer-info.component.html',
    styleUrls: ['./sol-properties-proposal-customer-info.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        MatRadioModule,
        MatFormFieldModule,
        MatInputModule,
        TranslatePipe
    ]
})
export class SolPropertiesProposalCustomerInfoComponent implements OnInit  {
    proposalCustomerInfoForm!: FormGroup<IProposalCustomerInfoFormGroup>
    private destroyRef = inject(DestroyRef);

    constructor(
        private readonly solPropertiesService: SolPropertiesService,
        private readonly commonDataService: CommonDataService
    ) { }

    ngOnInit(): void {
        this.initializeForm();
        this.subscribeFormValueChanges();
        this.setupReadOnlyMode();
    }

    private subscribeFormValueChanges(): void {
        this.proposalCustomerInfoForm.valueChanges
        .pipe(take(CONSTANTS.ONE))
        .subscribe(
            () => this.solPropertiesService.markPropertiesChanged()
        );
    }

    private setupReadOnlyMode(): void {
        this.commonDataService.readOnlyMode$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(isReadOnly => {
                if (this.solPropertiesService.isProposalReadOnly(isReadOnly)) {
                    this.proposalCustomerInfoForm.disable({ emitEvent: false });
                } else {
                    this.proposalCustomerInfoForm.enable({ emitEvent: false });
                    if (!this.proposalCustomerInfoForm.controls.customerOverrideOption.value) {
                        this.proposalCustomerInfoForm.controls.customAccountName.disable({ emitEvent: false });
                    }
                }
            });
    }

    initializeForm(): void {
        const otherMetaData = this.solPropertiesService.getOtherMetaDataModel();
        const estimateMetaData = this.solPropertiesService.getEstimateMetaDataModel();
        const customerInformation: CustomerInformation = otherMetaData.customerInformation;
        const estimateSettings = estimateMetaData?.estimateSettings;
        const customAccountName = estimateSettings?.customAccountName ?? CONSTANTS.EMPTY_STRING;
        const useCustomAccount = estimateSettings?.useCustomAccount ?? false;

        this.proposalCustomerInfoForm = new FormGroup<IProposalCustomerInfoFormGroup>({
            name: new FormControl<string>(customerInformation.endUserName ?? CONSTANTS.EMPTY_STRING),
            customAccountName: new FormControl<string>({ value: customAccountName, disabled: !useCustomAccount }),
            accountName: new FormControl<string>(customerInformation.customerName ?? CONSTANTS.EMPTY_STRING),
            customerOverrideOption: new FormControl<boolean>(useCustomAccount),
            address1: new FormControl<string>(customerInformation.custAddress1 ??CONSTANTS.EMPTY_STRING),
            address2: new FormControl<string>(customerInformation.custAddress2 ?? CONSTANTS.EMPTY_STRING),
            city: new FormControl<string>(customerInformation.custCity ?? CONSTANTS.EMPTY_STRING),
            state: new FormControl<string>(customerInformation.custState ?? CONSTANTS.EMPTY_STRING),
            zipCode: new FormControl<string>(customerInformation.custZipCode ?? CONSTANTS.EMPTY_STRING)
        });
        this.registerFormChangeHandlers(estimateMetaData);
    }

    private registerFormChangeHandlers(estimateMetaData: EstimateMetaData): void {
        const otherMetaData = this.solPropertiesService.getOtherMetaDataModel();
        const customerInfo = otherMetaData.customerInformation;
        const estimateSettings = estimateMetaData?.estimateSettings;

        this.proposalCustomerInfoForm.controls.customerOverrideOption.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(useCustomAccount => {
                if (useCustomAccount) {
                    this.proposalCustomerInfoForm.controls.customAccountName.enable({ emitEvent: false });
                } else {
                    this.proposalCustomerInfoForm.controls.customAccountName.disable({ emitEvent: false });
                }
            });

        this.proposalCustomerInfoForm.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                const values = this.proposalCustomerInfoForm.getRawValue();
                customerInfo.endUserName = values.name;
                customerInfo.custAddress1 = values.address1;
                customerInfo.custAddress2 = values.address2;
                customerInfo.custCity = values.city;
                customerInfo.custState = values.state;
                customerInfo.custZipCode = values.zipCode;
                if (estimateSettings) {
                    estimateSettings.customAccountName = values.customAccountName;
                    estimateSettings.useCustomAccount = values.customerOverrideOption;
                }
            });
    }

    get accountName(): string {
        return this.proposalCustomerInfoForm.controls.accountName.value || CONSTANTS.EMPTY_STRING;
    }

    get address1(): string {
        return this.proposalCustomerInfoForm.controls.address1.value || CONSTANTS.EMPTY_STRING;
    }

    get address2(): string {
        return this.proposalCustomerInfoForm.controls.address2.value || CONSTANTS.EMPTY_STRING;
    }

    get city(): string {
        return this.proposalCustomerInfoForm.controls.city.value || CONSTANTS.EMPTY_STRING;
    }

    get state(): string {
        return this.proposalCustomerInfoForm.controls.state.value || CONSTANTS.EMPTY_STRING;
    }

    get zipCode(): string {
        return this.proposalCustomerInfoForm.controls.zipCode.value || CONSTANTS.EMPTY_STRING;
    }

}